import React, { useEffect, useState } from 'react';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { SEO } from '../components/SEO';
import { ExternalLink, Calendar, Clock, ArrowRight, TrendingUp, Users, Award, BookOpen, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Resources = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlog, setLoadingBlog] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/blog-feed?limit=6`);
      const data = await response.json();
      if (data.success) {
        setBlogPosts(data.posts);
      }
    } catch (error) {
      // Silent fail - blog section will show empty state
    } finally {
      setLoadingBlog(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const industryStats = [
    { stat: '£3.5B+', label: 'UK Hair & Beauty Market Value' },
    { stat: '15M+', label: 'People with Hair Loss in UK' },
    { stat: '12%', label: 'Annual Industry Growth Rate' },
    { stat: '89%', label: 'Customer Retention in Specialist Salons' }
  ];

  const guides = [
    {
      title: 'Franchise Buyer\'s Guide',
      description: 'Everything you need to know before investing in a hair extensions franchise.',
      icon: BookOpen,
      link: 'https://customer-assets.emergentagent.com/job_agi-layer/artifacts/wrxcrc7r_NAHM%20Franchise%20Specialist%20Salon%20Brochure%20August%20%202025.pdf',
      linkText: 'Download Brochure'
    },
    {
      title: 'Hair Loss Solutions Overview',
      description: 'Understanding the different types of hair loss treatments and systems available.',
      icon: Users,
      link: '/hair-extensions-franchise-opportunity#faq',
      linkText: 'Learn More'
    },
    {
      title: 'Industry Trends 2026',
      description: 'Key trends shaping the hair extensions and specialist salon industry.',
      icon: TrendingUp,
      link: 'https://www.needahairmakeover.blog',
      linkText: 'Read Blog'
    },
    {
      title: 'Success Metrics Guide',
      description: 'How to measure and achieve success as a salon franchise owner.',
      icon: Award,
      link: '/hair-extensions-franchise-opportunity',
      linkText: 'View Opportunity'
    }
  ];

  const externalResources = [
    {
      name: 'British Franchise Association',
      description: 'Official franchise industry body providing guidance and accreditation.',
      url: 'https://www.thebfa.org',
      type: 'Industry Body'
    },
    {
      name: 'Hair Extensions Training Academy',
      description: 'Professional training courses for hair extension techniques.',
      url: 'https://www.hairextensionstraining.academy',
      type: 'Training'
    },
    {
      name: 'Need A Hair Makeover Blog',
      description: 'Latest news, tips, and insights from our team.',
      url: 'https://www.needahairmakeover.blog',
      type: 'Blog'
    },
    {
      name: 'Book an Appointment',
      description: 'Experience our services at the flagship salon.',
      url: 'https://www.needahairmakeover.com',
      type: 'Booking'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SEO page="resources" />
      <Header onApplyClick={() => window.location.href = '/#apply-form'} />
      
      {/* Hero Section */}
      <div className="bg-dark text-white pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Resources & Insights</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Industry research, guides, and insights to help you understand the hair extensions 
              and hair loss specialist market.
            </p>
          </div>
        </div>
      </div>

      <div className="py-16">
        <div className="container mx-auto px-6 max-w-6xl">
          
          {/* Industry Statistics */}
          <div className="bg-accent-primary rounded-2xl p-8 mb-12 -mt-20 relative z-10 shadow-2xl">
            <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: 'white' }}>Industry at a Glance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {industryStats.map((item, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold mb-2" style={{ color: 'white' }}>{item.stat}</div>
                  <div className="text-sm opacity-90" style={{ color: 'white' }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Live Blog Feed */}
          <div className="mb-16">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-dark">Latest from Our Blog</h2>
              <a 
                href="https://www.needahairmakeover.blog" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-accent-primary hover:underline flex items-center font-semibold"
              >
                View All Articles <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
            
            {loadingBlog ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
                <span className="ml-3 text-subtle">Loading latest posts...</span>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8">
                {blogPosts.slice(0, 6).map((post, index) => (
                  <a 
                    key={index}
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 group"
                  >
                    <div className="h-48 overflow-hidden bg-gray-100">
                      {post.image ? (
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-accent-primary/10">
                          <BookOpen className="w-12 h-12 text-accent-primary/50" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-accent-primary bg-accent-primary/10 px-3 py-1 rounded-full">
                          {post.categories?.[0] || 'Article'}
                        </span>
                        <span className="text-xs text-subtle flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(post.pubDate)}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-dark mb-2 group-hover:text-accent-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-subtle text-sm line-clamp-3">{post.description}</p>
                      <div className="mt-4 text-sm text-accent-primary font-semibold flex items-center">
                        Read More <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Guides & Downloads */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-8">Guides & Resources</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {guides.map((guide, index) => (
                <a 
                  key={index} 
                  href={guide.link}
                  target={guide.link.startsWith('http') ? '_blank' : '_self'}
                  rel={guide.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="flex items-start p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                >
                  <div className="w-12 h-12 bg-accent-primary/10 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <guide.icon className="w-6 h-6 text-accent-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-dark mb-2 group-hover:text-accent-primary transition-colors">{guide.title}</h3>
                    <p className="text-subtle text-sm mb-3">{guide.description}</p>
                    <span className="text-accent-primary font-semibold text-sm flex items-center">
                      {guide.linkText} <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
            <div className="mt-8 text-center">
              <a 
                href="/#apply-form"
                className="inline-flex items-center bg-accent-primary hover:bg-accent-primary/90 font-semibold px-8 py-3 rounded-full transition-colors"
                style={{ color: 'white' }}
              >
                Request Information Pack
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>

          {/* Why Invest Section - SEO Content with Video */}
          <div className="bg-gradient-to-r from-accent-primary/5 to-accent-primary/10 rounded-2xl p-8 mb-12 border border-accent-primary/20">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Video - Left Side */}
              <div className="flex justify-start">
                <div className="w-full max-w-[350px] aspect-[9/16] rounded-xl overflow-hidden shadow-xl">
                  <iframe
                    src="https://www.youtube.com/embed/S2nVagNU55A"
                    title="Why invest in a hair extensions business"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
              
              {/* Content - Right Side */}
              <div>
                <h2 className="text-3xl font-bold text-dark mb-6">Why Invest in a Hair Extensions Franchise?</h2>
                <div className="prose prose-lg max-w-none text-subtle">
                  <p className="mb-4">
                    The UK hair extensions market represents one of the most exciting opportunities in the beauty industry today. 
                    With over <strong>15 million people experiencing hair loss</strong> in the UK alone, and a growing demand for 
                    premium hair extension services, specialist salons are positioned for exceptional growth.
                  </p>
                  <p className="mb-4">
                    Unlike traditional salons, specialist hair extension and hair loss franchises benefit from:
                  </p>
                  <ul className="list-disc pl-6 mb-4 space-y-2">
                    <li><strong>Higher average transaction values</strong> - Specialist services command premium pricing</li>
                    <li><strong>Recurring revenue</strong> - Clients return for maintenance and upgrades every 6-8 weeks</li>
                    <li><strong>Recession resistance</strong> - Personal appearance services remain in demand during economic downturns</li>
                    <li><strong>Lower competition</strong> - Fewer specialists compared to general salons</li>
                    <li><strong>Emotional value</strong> - Hair restoration services create life-changing transformations</li>
                  </ul>
                  <p>
                    Need A Hair Makeover® offers a proven franchise model with comprehensive training, ongoing support, 
                    and a nationally recognized brand that has won multiple industry awards since 2017.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quiz Assessment Banner */}
          <a 
            href="https://www.qualiquiz.app/quiz/salon-franchise-candidate-pre-qualification-asses"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full cursor-pointer hover:opacity-95 transition-opacity mb-12"
            data-testid="quiz-banner-resources"
          >
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_f194cab7-0920-47a1-bac9-625d43d4e059/artifacts/9uxsk4g1_file_00000000e60c71fd9260b152b27c6a16.png"
              alt="Salon Franchise Candidate Pre-Qualification Assessment - Are you ready to own a specialist salon? Take the Assessment"
              className="w-full h-auto rounded-2xl shadow-lg"
            />
          </a>

          {/* External Resources */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-8">Useful Links</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {externalResources.map((resource, index) => (
                <a 
                  key={index}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-accent-primary hover:bg-accent-primary/5 transition-all group"
                >
                  <div>
                    <span className="text-xs font-semibold text-accent-primary bg-accent-primary/10 px-2 py-1 rounded mb-2 inline-block">
                      {resource.type}
                    </span>
                    <h3 className="font-bold text-dark group-hover:text-accent-primary transition-colors">{resource.name}</h3>
                    <p className="text-sm text-subtle">{resource.description}</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-accent-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-dark text-white rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Learn More?</h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Download our comprehensive franchise brochure or speak with our team to discover 
              how you can join the Need A Hair Makeover success story.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/#apply-form"
                className="inline-flex items-center justify-center bg-accent-primary hover:bg-accent-primary/90 px-8 py-3 rounded-full font-semibold transition-colors"
                style={{ color: 'white' }}
              >
                Get Franchise Brochure
              </a>
              <a 
                href="/hair-extensions-franchise-opportunity"
                className="inline-flex items-center justify-center bg-white text-dark hover:bg-gray-100 px-8 py-3 rounded-full font-semibold transition-colors"
              >
                View Opportunity Details
              </a>
            </div>
          </div>

        </div>
      </div>
      
      <Footer />
    </div>
  );
};
